import { WorkflowsCreatePayload } from '@api/types.gen';

import {
  StaggerConfig,
  StaggerOption,
  StaggerParallelOption,
  UpdateFailureAction,
} from '@/react/edge/edge-stacks/components/StaggerFieldset.types';
import { RelativePathModel } from '@/react/portainer/gitops/RelativePathFieldset/types';

import { Artifact, FormValues } from './type';

export function toCreateWorkflowPayload(
  values: FormValues
): WorkflowsCreatePayload {
  return {
    name: values.name,
    artifacts: values.artifacts.map((artifact) => toArtifactPayload(artifact)),
  };
}

function toArtifactPayload(
  artifact: Artifact
): WorkflowsCreatePayload['artifacts'][number] {
  return {
    name: artifact.name,
    deploymentType: artifact.deploymentType,
    files: artifact.files.map((file) => ({
      sourceId: file.sourceId,
      path: file.path,
      ref: file.ref,
    })),
    targets: { edgeGroups: artifact.targets },
    config: {
      prePullImage: artifact.config.prePullImage,
      retryPeriod: artifact.config.retryDeploy
        ? artifact.config.retryPeriod
        : undefined,
      envVars: artifact.config.envVars
        .filter((envVar) => !envVar.needsDeletion)
        .map((envVar) => ({ name: envVar.name, value: envVar.value ?? '' })),
      parallelConfig: toParallelConfig(artifact.config.parallelConfig),
      registries: artifact.config.registryId
        ? [artifact.config.registryId]
        : undefined,
      useManifestNamespaces: artifact.config.useManifestNamespaces,
      ...toRelativePathConfig(artifact.config.relativePath),
    },
  };
}

function toRelativePathConfig(relativePath: RelativePathModel) {
  return {
    ...(relativePath.SupportRelativePath
      ? {
          alwaysCloneGitRepoForRelativePath:
            relativePath.AlwaysCloneGitRepoForRelativePath,
          localFilesystemPath: relativePath.FilesystemPath,
        }
      : {}),
    ...(relativePath.SupportPerDeviceConfigs
      ? {
          perDeviceConfigsPath: relativePath.PerDeviceConfigsPath,
          perDeviceConfigsMatchType:
            relativePath.PerDeviceConfigsMatchType || undefined,
          perDeviceConfigsGroupMatchType:
            relativePath.PerDeviceConfigsGroupMatchType || undefined,
        }
      : {}),
  };
}

function toParallelConfig(config: StaggerConfig) {
  if (config.StaggerOption !== StaggerOption.Parallel) {
    return undefined;
  }

  return {
    batchCount: config.DeviceNumber,
    batchIncrementBy:
      config.StaggerParallelOption === StaggerParallelOption.Incremental
        ? config.DeviceNumberIncrementBy
        : undefined,
    delay: config.UpdateDelay,
    timeout: config.Timeout,
    failureAction: toFailureAction(config.UpdateFailureAction),
  };
}

function toFailureAction(action: UpdateFailureAction | undefined) {
  if (action === UpdateFailureAction.Pause) {
    return 'pause';
  }

  if (action === UpdateFailureAction.Rollback) {
    return 'rollback';
  }

  return 'continue';
}
